/// <reference types="@angular/localize" />
import { Service, signal } from '@angular/core';

export type ConfirmDialogTone = 'default' | 'destructive';

/** Where the head — icon, title, description — sits. */
export type ConfirmDialogAlign = 'center' | 'start';

/**
 * How a question ended. `declined` is the labelled cancel button, which is an answer
 * of its own where the two buttons are two courses of action — «Nur diese Aufgabe»
 * against «Übertragen». `dismissed` is escape, the backdrop or the close icon, which
 * answer nothing and must not be read as either.
 */
export type ConfirmDialogAnswer = 'confirmed' | 'declined' | 'dismissed';

export interface ConfirmDialogOptions {
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  /** `destructive` colours the confirm button red. */
  tone: ConfirmDialogTone;
  /**
   * Head alignment. Centred unless the question carries a body of its own: a centred
   * icon and title over a left-aligned {@link details} list reads as two designs.
   */
  align: ConfirmDialogAlign;
  /** Name of a registered `dm-icon`. */
  icon: string;
  /**
   * Label of a third button that answers nothing and closes the dialog.
   *
   * For a question whose two buttons are two courses of action: without it the only
   * way out is escape or the close icon, which is not an offer.
   */
  dismissLabel?: string;
  /**
   * What the question applies to, one entry per line under the description.
   *
   * Appending them to the description instead runs them into its last sentence: the
   * text is one string prop on the header, so a line break in it collapses.
   *
   * Pair them with `align: 'start'`, so head and list share one margin.
   */
  details: readonly string[];
  /**
   * A narrower box, for a question that is two lines and three buttons rather than a
   * paragraph and a list.
   *
   * At that width the design system's footer stacks itself, which is the point: three
   * actions in a row are cramped, and stacked they read in order. Leave it off for a
   * question with `details`, where the list wants the room.
   */
  compact: boolean;
  /**
   * Where focus goes once the question has closed: an element, or a selector resolved then.
   * Unset, it returns to the element that asked, or near it where that is gone. Set it where the
   * answer removes the control that asked, such as an edit mode's cancel button.
   */
  returnFocusTo?: HTMLElement | string;
}

/** What a caller passes: title and description, everything else optional. */
export type ConfirmDialogInput = Partial<ConfirmDialogOptions> & Pick<ConfirmDialogOptions, 'title' | 'description'>;

interface PendingConfirmation {
  options: ConfirmDialogOptions;
  answer: (answer: ConfirmDialogAnswer) => void;
}

/**
 * Asks a blocking question and resolves with the answer. Rendered by
 * `ConfirmDialogComponent`, which sits once in the app shell.
 *
 * For the named intents — {@link confirmDelete}, {@link confirmDiscardChanges} — the
 * subject of the question is enough; tone, icon and labels are fixed there.
 * {@link confirm} is for questions that are neither.
 *
 * @example if (!await this.confirmDialogService.confirmDiscardChanges()) { return; }
 */
@Service()
export class ConfirmDialogService {
  /** The pending question, read by the dialog component. */
  public readonly pending = signal<PendingConfirmation | null>(null);

  private readonly defaultOptions: Omit<ConfirmDialogOptions, 'title' | 'description'> = {
    confirmLabel: $localize`:@@common.confirm:Bestätigen`,
    cancelLabel: $localize`:@@common.cancel:Abbrechen`,
    tone: 'default',
    align: 'center',
    icon: 'alert-triangle',
    details: [],
    compact: false,
  };

  /**
   * @param {ConfirmDialogInput} options - title and description are mandatory
   * @returns {Promise<boolean>} Whether the user confirmed; anything else is a no
   */
  public async confirm(options: ConfirmDialogInput): Promise<boolean> {
    return (await this.ask(options)) === 'confirmed';
  }

  /**
   * The same question, telling the cancel button apart from a dismissal. For the
   * dialogs whose two buttons are two courses of action rather than yes and no.
   *
   * @param {ConfirmDialogInput} options - title and description are mandatory
   * @returns {Promise<ConfirmDialogAnswer>} How the user ended it
   */
  public ask(options: ConfirmDialogInput): Promise<ConfirmDialogAnswer> {
    // Two questions at once are indistinguishable; the first one is dropped.
    this.pending()?.answer('dismissed');

    return new Promise<ConfirmDialogAnswer>((resolve) => {
      this.pending.set({
        options: { ...this.defaultOptions, ...options },
        answer: (answer: ConfirmDialogAnswer) => {
          this.pending.set(null);
          resolve(answer);
        },
      });
    });
  }

  /** Drops a pending question once its subject is gone. */
  public dismiss(): void {
    this.pending()?.answer('dismissed');
  }

  /**
   * Permanent deletion.
   *
   * @param {ConfirmDialogInput} options - title and description; anything else overrides the intent
   * @returns {Promise<boolean>} Whether to delete
   */
  public confirmDelete(options: ConfirmDialogInput): Promise<boolean> {
    return this.confirm({
      confirmLabel: $localize`:@@common.delete:Löschen`,
      tone: 'destructive',
      icon: 'trash-01',
      ...options,
    });
  }

  /**
   * Discarding input that was never saved.
   *
   * @param {string} [description] - names what will be lost, replacing the default
   * @param {Pick<ConfirmDialogOptions, 'returnFocusTo'>} [options] - where focus goes once answered,
   *   see {@link ConfirmDialogOptions.returnFocusTo}
   * @returns {Promise<boolean>} Whether to discard
   */
  public confirmDiscardChanges(description?: string, options: Pick<ConfirmDialogOptions, 'returnFocusTo'> = {}): Promise<boolean> {
    return this.confirm({
      ...options,
      title: $localize`:@@common.discardChanges:Änderungen verwerfen?`,
      description: description ?? $localize`:@@common.discardChangesDescription:Die Eingaben werden nicht gespeichert.`,
      confirmLabel: $localize`:@@common.discard:Verwerfen`,
      tone: 'destructive',
      icon: 'alert-triangle',
    });
  }
}
